/**
 * The graph view's model: pages as nodes, [[links]] (and optionally the
 * page tree) as edges, filtered and, for the local graph, cut to the pages
 * within a few steps of one page. Pure data; the app draws it.
 */
import type { Id } from './model';

export interface GraphPage {
  id: Id;
  title: string;
  parentId: Id | null;
  /** Hidden from AI (ai: never, or under such a page). */
  aiHidden?: boolean;
}

export interface GraphNode {
  id: Id;
  title: string;
  /** The top-level page it lives under (its "area"), for colours. */
  area: Id;
  degree: number;
  tags: string[];
}

export interface GraphEdge {
  source: Id;
  target: Id;
  kind: 'link' | 'parent';
}

export interface GraphOptions {
  /** Draw the page tree as well (folder → page). */
  hierarchy?: boolean;
  /** Keep pages without any edge. */
  orphans?: boolean;
  /** Leave out pages hidden from AI. */
  hideAiHidden?: boolean;
  /** Only pages with this tag (lower case, without #). */
  tag?: string | null;
  /** Only pages under this top-level page. */
  area?: Id | null;
  /** Local graph: only pages within `depth` steps of this page. */
  center?: Id | null;
  depth?: number;
}

export interface Graph {
  nodes: GraphNode[];
  edges: GraphEdge[];
}

export function buildGraph(
  pages: GraphPage[],
  links: { src: Id; dst: Id }[],
  tags: { pageId: Id; tag: string }[],
  opts: GraphOptions = {},
): Graph {
  const byId = new Map(pages.map((p) => [p.id, p]));
  const areaOf = new Map<Id, Id>();
  const area = (id: Id): Id => {
    const known = areaOf.get(id);
    if (known) return known;
    let cur = byId.get(id)!;
    for (let i = 0; cur.parentId && byId.has(cur.parentId) && i < 100; i++)
      cur = byId.get(cur.parentId)!;
    areaOf.set(id, cur.id);
    return cur.id;
  };
  const tagsOf = new Map<Id, string[]>();
  for (const t of tags) {
    const list = tagsOf.get(t.pageId) ?? [];
    if (!list.includes(t.tag)) list.push(t.tag);
    tagsOf.set(t.pageId, list);
  }

  // Which pages may appear at all.
  const keep = new Set(
    pages
      .filter((p) => !(opts.hideAiHidden && p.aiHidden))
      .filter((p) => !opts.area || area(p.id) === opts.area)
      .filter((p) => !opts.tag || (tagsOf.get(p.id) ?? []).includes(opts.tag))
      .map((p) => p.id),
  );

  const seen = new Set<string>();
  let edges: GraphEdge[] = [];
  const add = (source: Id, target: Id, kind: GraphEdge['kind']) => {
    if (!keep.has(source) || !keep.has(target) || source === target) return;
    // One edge per pair, whatever its direction.
    const key = source < target ? `${source}|${target}` : `${target}|${source}`;
    if (seen.has(key)) return;
    seen.add(key);
    edges.push({ source, target, kind });
  };
  for (const l of links) add(l.src, l.dst, 'link');
  if (opts.hierarchy) for (const p of pages) if (p.parentId) add(p.parentId, p.id, 'parent');

  let ids = keep;
  if (opts.center && keep.has(opts.center)) {
    // Breadth-first from the centre over the edges kept so far.
    const next = new Map<Id, Id[]>();
    for (const e of edges) {
      next.set(e.source, [...(next.get(e.source) ?? []), e.target]);
      next.set(e.target, [...(next.get(e.target) ?? []), e.source]);
    }
    const near = new Set<Id>([opts.center]);
    let frontier = [opts.center];
    for (let d = 0; d < (opts.depth ?? 1); d++) {
      frontier = frontier.flatMap((id) => next.get(id) ?? []).filter((id) => !near.has(id));
      for (const id of frontier) near.add(id);
    }
    ids = near;
    edges = edges.filter((e) => near.has(e.source) && near.has(e.target));
  }

  const degree = new Map<Id, number>();
  for (const e of edges) {
    degree.set(e.source, (degree.get(e.source) ?? 0) + 1);
    degree.set(e.target, (degree.get(e.target) ?? 0) + 1);
  }
  const nodes = pages
    .filter((p) => ids.has(p.id))
    .filter((p) => opts.orphans || opts.center === p.id || (degree.get(p.id) ?? 0) > 0)
    .map((p) => ({
      id: p.id,
      title: p.title,
      area: area(p.id),
      degree: degree.get(p.id) ?? 0,
      tags: tagsOf.get(p.id) ?? [],
    }));
  return { nodes, edges };
}
